/**
 * Generieke (domein-onafhankelijke) SVG-geometrie-helpers voor het Mandala-wiel.
 *
 * Hoek-conventie: alle hoeken zijn in graden, gemeten **met de klok mee vanaf
 * de top (12 uur)**. Dus 0° = boven, 90° = rechts (3 uur), 180° = onder,
 * 270° = links (9 uur). Deze conventie sluit aan op de gangbare SVG-arc-helper
 * (`(angle - 90)` in radialen) en wordt centraal in {@link polarToCartesian}
 * vastgelegd. De HD-specifieke "lengte → schermhoek" mapping (counterclockwise)
 * leeft in `mandala-layout.ts`.
 */

export interface MandalaPoint {
  x: number;
  y: number;
}

const DEGREES_IN_CIRCLE = 360;
const PATH_PRECISION = 3;

export function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Normaliseert een hoek (graden) naar het halfopen interval [0, 360).
 */
export function normalizeAngle(angleDeg: number): number {
  return ((angleDeg % DEGREES_IN_CIRCLE) + DEGREES_IN_CIRCLE) % DEGREES_IN_CIRCLE;
}

/**
 * Zet een poolcoördinaat om naar een cartesische coördinaat.
 *
 * @param cx middelpunt X
 * @param cy middelpunt Y
 * @param radius straal
 * @param angleDeg hoek in graden, met de klok mee vanaf de top (zie module-doc)
 */
export function polarToCartesian(cx: number, cy: number, radius: number, angleDeg: number): MandalaPoint {
  const angleRad = degToRad(angleDeg - 90);
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

function round(value: number): number {
  const factor = 10 ** PATH_PRECISION;
  return Math.round(value * factor) / factor;
}

/**
 * Bouwt het SVG-`d`-pad voor een ringsegment (annular sector) tussen
 * `innerRadius` en `outerRadius`, lopend van `startAngle` naar `endAngle`.
 *
 * Vereiste: `endAngle >= startAngle` (de aanroeper normaliseert zodat het
 * segment in toenemende schermhoek-richting loopt). De segmentbreedte mag
 * tot 360° zijn; >180° wordt via de large-arc-flag correct getekend.
 */
export function describeAnnularSector(
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number,
): string {
  const outerStart = polarToCartesian(cx, cy, outerRadius, endAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, startAngle);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, startAngle);
  const innerStart = polarToCartesian(cx, cy, innerRadius, endAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

  return [
    'M', round(outerStart.x), round(outerStart.y),
    'A', round(outerRadius), round(outerRadius), 0, largeArcFlag, 0, round(outerEnd.x), round(outerEnd.y),
    'L', round(innerEnd.x), round(innerEnd.y),
    'A', round(innerRadius), round(innerRadius), 0, largeArcFlag, 1, round(innerStart.x), round(innerStart.y),
    'Z',
  ].join(' ');
}

/**
 * Bouwt het SVG-`d`-pad voor een "spaak": een radiale lijn van het middelpunt
 * (of `innerRadius`) naar `outerRadius` op de gegeven schermhoek.
 */
export function describeSpoke(
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  angleDeg: number,
): string {
  const inner = polarToCartesian(cx, cy, innerRadius, angleDeg);
  const outer = polarToCartesian(cx, cy, outerRadius, angleDeg);
  return ['M', round(inner.x), round(inner.y), 'L', round(outer.x), round(outer.y)].join(' ');
}
